/**
 * Stage 03 — Organisms section for the Atomic Design showcase.
 * Sidebar, TopBar, ApprovalsTable, ConflictResolver, AIPanel,
 * Calendar, BalanceRing, Card, CommandPalette, Modal.
 */

import { Avatar } from '../../components/primitives/Avatar'
import { Badge } from '../../components/primitives/Badge'
import { Button } from '../../components/primitives/Button'
import { Icon } from '../../components/primitives/Icon'
import { IlmuMark } from '../../components/primitives/IlmuMark'
import { Kbd } from '../../components/primitives/Kbd'
import { Grid2, PrimitiveCard, SectionHeader } from './shared'

export function OrganismsSection() {
  return (
    <section id="organisms" style={{ marginBottom: 80, scrollMarginTop: 24 }}>
      <SectionHeader
        num="03"
        name="Organisms"
        tag="Distinct sections"
        stage="organisms"
        desc="Self-contained UI regions made from groups of molecules and atoms — the sidebar, a card with a header and table, the AI panel with its alternatives."
      />

      <Grid2>
        {/* Sidebar */}
        <PrimitiveCard
          name="Sidebar"
          tag="Organism"
          desc="Brand + role switcher + grouped nav. Sticky, full-height."
          uses={[
            { stage: 'molecule', label: 'Nav item' },
            { stage: 'molecule', label: 'User chip' },
          ]}
        >
          <div
            style={{
              width: 170,
              height: 240,
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 10,
              padding: 10,
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              fontSize: 12,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 6px' }}>
              <span style={{ width: 22, height: 22, borderRadius: 6, background: 'var(--space-cadet)' }} />
              <b style={{ fontSize: 12, fontWeight: 600 }}>Lumen</b>
            </div>
            {[
              { grp: 'Personal', items: ['Home', 'My leaves', 'Apply'] },
              { grp: 'Manager', items: ['Approvals', 'Patterns'] },
            ].map(({ grp, items }) => (
              <div key={grp}>
                <div
                  style={{
                    fontSize: 9,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    color: 'var(--text-3)',
                    padding: '8px 6px 2px',
                    fontWeight: 600,
                    fontFamily: 'var(--font-mono)',
                  }}
                >
                  {grp}
                </div>
                {items.map((item, i) => (
                  <div
                    key={item}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '5px 6px',
                      borderRadius: 5,
                      color: i === 0 && grp === 'Personal' ? 'var(--steel-blue-3)' : 'var(--text-2)',
                      background:
                        i === 0 && grp === 'Personal' ? 'var(--baby-blue-tint)' : 'transparent',
                      fontSize: 11,
                    }}
                  >
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: 3,
                        background:
                          i === 0 && grp === 'Personal' ? 'var(--steel-blue)' : 'var(--text-3)',
                        opacity: i === 0 && grp === 'Personal' ? 1 : 0.4,
                      }}
                    />
                    {item}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </PrimitiveCard>

        {/* Top bar */}
        <PrimitiveCard
          name="Top bar"
          tag="Organism"
          desc="Breadcrumb + search + persona + bell. 60 px sticky."
          uses={[
            { stage: 'molecule', label: 'Breadcrumb' },
            { stage: 'molecule', label: 'Search field' },
            { stage: 'atom', label: 'Avatar' },
          ]}
        >
          <div
            style={{
              height: 44,
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 9,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '0 14px',
              width: '100%',
              fontSize: 12,
              color: 'var(--text-2)',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
              <span>Home</span>
              <span style={{ color: 'var(--text-3)' }}>›</span>
              <b style={{ color: 'var(--text)', fontWeight: 500 }}>Approvals</b>
            </span>
            <div
              style={{
                marginLeft: 'auto',
                background: 'var(--surface-2)',
                borderRadius: 7,
                padding: '5px 10px',
                fontSize: 11.5,
                color: 'var(--text-3)',
                display: 'flex',
                gap: 6,
                alignItems: 'center',
                width: 200,
              }}
            >
              <Icon name="search" size={12} />
              Search or ask ILMU
              <Kbd>⌘K</Kbd>
            </div>
            <Avatar name="Hafiz Zainudin" size={24} />
          </div>
        </PrimitiveCard>

        {/* Approvals table */}
        <PrimitiveCard
          name="Approvals table"
          tag="Organism"
          desc="Request rows with status, dates, and a conflict signal."
          noPad
          uses={[
            { stage: 'molecule', label: 'User chip' },
            { stage: 'atom', label: 'Badge' },
          ]}
        >
          <table
            style={{
              width: '100%',
              borderCollapse: 'separate',
              borderSpacing: 0,
              fontSize: 13,
            }}
          >
            <thead>
              <tr>
                {['Person', 'Dates', 'Status'].map((h) => (
                  <th
                    key={h}
                    style={{
                      textAlign: 'left',
                      padding: '10px 14px',
                      background: 'var(--surface-2)',
                      fontWeight: 500,
                      fontSize: 11,
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      color: 'var(--text-2)',
                      borderBottom: '1px solid var(--border)',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                { name: 'Aisyah', dates: 'Jun 22 – 26', variant: 'warning' as const, label: 'Pending' },
                { name: 'Daniel', dates: 'Jun 22 – 24', variant: 'danger' as const, label: 'Conflict' },
                { name: 'Suresh', dates: 'Jul 6 – 8', variant: 'success' as const, label: 'Approved' },
              ].map(({ name, dates, variant, label }, i) => (
                <tr key={name}>
                  <td
                    style={{
                      padding: '11px 14px',
                      borderBottom: i < 2 ? '1px solid var(--border)' : undefined,
                    }}
                  >
                    <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <Avatar name={name} size={24} />
                      {name}
                    </span>
                  </td>
                  <td
                    style={{
                      padding: '11px 14px',
                      borderBottom: i < 2 ? '1px solid var(--border)' : undefined,
                    }}
                  >
                    {dates}
                  </td>
                  <td
                    style={{
                      padding: '11px 14px',
                      borderBottom: i < 2 ? '1px solid var(--border)' : undefined,
                    }}
                  >
                    <Badge variant={variant} dot>{label}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </PrimitiveCard>

        {/* Conflict resolver */}
        <PrimitiveCard
          name="Conflict resolver"
          tag="Organism"
          desc="Warning header, the two requesters scored, a final pick."
          noPad
          uses={[
            { stage: 'molecule', label: 'User chip' },
            { stage: 'atom', label: 'Badge' },
            { stage: 'atom', label: 'Icon' },
          ]}
        >
          <div>
            <div
              style={{
                padding: '12px 16px',
                background: '#FFF7E8',
                borderBottom: '1px solid rgba(194,138,45,.2)',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 13,
              }}
            >
              <Icon name="warning" size={14} style={{ color: '#84601F' }} />
              <b style={{ color: '#84601F' }}>Peak-period conflict · Hari Raya week</b>
            </div>
            {[
              { name: 'Aisyah', score: 73, badge: { variant: 'success' as const, label: 'Winner' } },
              { name: 'Daniel', score: 52, badge: { variant: 'default' as const, label: 'Alt offered' } },
            ].map(({ name, score, badge }, i) => (
              <div
                key={name}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto',
                  gap: 12,
                  padding: '12px 16px',
                  borderBottom: i === 0 ? '1px solid var(--border)' : undefined,
                  alignItems: 'center',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Avatar name={name} size={24} />
                  <span>
                    <b>{name}</b> · score{' '}
                    <span style={{ fontFamily: 'var(--font-mono)' }}>{score}</span>
                  </span>
                </span>
                <Badge variant={badge.variant}>{badge.label}</Badge>
              </div>
            ))}
          </div>
        </PrimitiveCard>

        {/* AI panel */}
        <PrimitiveCard
          name="AI panel"
          tag="Organism · AI"
          desc="Dark navy with a Pear radial. ILMU's recommendation + 2-3 alternatives."
          uses={[
            { stage: 'molecule', label: 'Tool call' },
            { stage: 'molecule', label: 'ILMU chip' },
          ]}
        >
          <div
            style={{
              background: 'linear-gradient(180deg,#1A2240 0%, var(--space-cadet) 100%)',
              color: '#fff',
              borderRadius: 14,
              position: 'relative',
              overflow: 'hidden',
              width: '100%',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: -40,
                right: -40,
                width: 180,
                height: 180,
                background: 'radial-gradient(circle, var(--pear) 0%, transparent 60%)',
                opacity: 0.18,
                pointerEvents: 'none',
              }}
            />
            <div
              style={{
                padding: '14px 18px',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                borderBottom: '1px solid rgba(255,255,255,.06)',
                position: 'relative',
              }}
            >
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '3px 9px 3px 5px',
                  borderRadius: 999,
                  background: 'rgba(255,255,255,.08)',
                  color: '#fff',
                  fontSize: 10.5,
                  fontWeight: 700,
                  fontFamily: 'var(--font-mono)',
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  border: '1px solid rgba(255,255,255,.1)',
                }}
              >
                <IlmuMark size={13} aria-hidden />
                ILMU
              </span>
              <span style={{ fontWeight: 600, fontSize: 13 }}>Cleanest 5-day window</span>
            </div>
            <div style={{ padding: '16px 18px', fontSize: 13, lineHeight: 1.55, position: 'relative' }}>
              {[
                { label: 'Jun 29 – Jul 3', meta: '20% absent', rec: false },
                { label: 'Oct 19 – 23', meta: 'Recommended · 10%', rec: true },
                { label: 'Nov 9 – 13', meta: '20% absent', rec: false },
              ].map(({ label, meta, rec }) => (
                <div
                  key={label}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '9px 12px',
                    background: rec ? 'rgba(205,222,0,.14)' : 'rgba(255,255,255,.06)',
                    borderRadius: 9,
                    fontSize: 12,
                    marginBottom: 6,
                    border: rec ? '1px solid rgba(205,222,0,.3)' : undefined,
                  }}
                >
                  <span>{label}</span>
                  <span
                    style={
                      rec
                        ? {
                            color: 'var(--pear)',
                            fontFamily: 'var(--font-mono)',
                            fontSize: 10,
                            textTransform: 'uppercase',
                            letterSpacing: '0.06em',
                            fontWeight: 600,
                          }
                        : undefined
                    }
                  >
                    {meta}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </PrimitiveCard>

        {/* Calendar organism */}
        <PrimitiveCard
          name="Calendar"
          tag="Organism"
          desc="7-column grid of cells, holidays + blackouts overlaid."
          tight
          uses={[{ stage: 'molecule', label: 'Calendar cell' }]}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: 4,
              width: '100%',
            }}
          >
            {[
              { day: 29, muted: true }, { day: 30, muted: true },
              { day: 1 }, { day: 2 }, { day: 3 },
              { day: 4, weekend: true }, { day: 5, weekend: true },
              { day: 6 }, { day: 7, evt: 'A · AL' },
              { day: 8 }, { day: 9 }, { day: 10 },
              { day: 11, weekend: true }, { day: 12, weekend: true },
              { day: 20, holiday: true, evt: 'Hari Raya' },
              { day: 21, holiday: true, evt: 'Hari Raya' },
              { day: 22, today: true, evt: 'A · AL' },
              { day: 23 }, { day: 24 },
              { day: 25, weekend: true }, { day: 26, weekend: true },
            ].map((cell, i) => {
              const bg = cell.holiday
                ? 'var(--french-gray-2)'
                : cell.muted || cell.weekend
                  ? 'var(--surface-2)'
                  : 'var(--surface)'
              const clr = cell.holiday
                ? 'var(--payne)'
                : cell.muted
                  ? 'var(--text-3)'
                  : 'var(--text-2)'
              return (
                <div
                  key={i}
                  style={{
                    aspectRatio: '1.05',
                    background: bg,
                    border: `${cell.today ? '1.5px' : '1px'} solid ${cell.today ? 'var(--space-cadet)' : 'var(--border)'}`,
                    borderRadius: 7,
                    padding: '5px 6px',
                    fontSize: 10.5,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                    color: clr,
                  }}
                >
                  <span style={{ fontSize: 10, fontWeight: 500 }}>{cell.day}</span>
                  {cell.evt ? (
                    <span
                      style={{
                        fontSize: 9,
                        padding: '1px 4px',
                        borderRadius: 3,
                        background: cell.holiday ? '#0082CB' : 'var(--baby-blue-tint)',
                        color: cell.holiday ? '#fff' : 'var(--steel-blue-3)',
                        fontWeight: 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {cell.evt}
                    </span>
                  ) : null}
                </div>
              )
            })}
          </div>
        </PrimitiveCard>

        {/* Balance ring */}
        <PrimitiveCard
          name="Balance ring"
          tag="Organism"
          desc="SVG ring + center stack. Used for any 0–100% balance."
          uses={[{ stage: 'molecule', label: 'Stat' }]}
        >
          <div style={{ position: 'relative', display: 'inline-block', width: 120, height: 120 }}>
            <svg width="120" height="120">
              <circle cx="60" cy="60" r="52" stroke="var(--surface-2)" strokeWidth="10" fill="none" />
              <circle
                cx="60"
                cy="60"
                r="52"
                stroke="var(--steel-blue)"
                strokeWidth="10"
                fill="none"
                strokeDasharray="326.7"
                strokeDashoffset="98"
                strokeLinecap="round"
                transform="rotate(-90 60 60)"
              />
            </svg>
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <div
                style={{
                  fontSize: 26,
                  fontWeight: 600,
                  letterSpacing: '-0.02em',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                12.5
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-2)' }}>of 18 days</div>
            </div>
          </div>
        </PrimitiveCard>

        {/* Card organism */}
        <PrimitiveCard
          name="Card"
          tag="Organism"
          desc="Header (title + meta) over a body. The container for almost everything."
          uses={[{ stage: 'atom', label: 'Badge' }]}
        >
          <div
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 14,
              overflow: 'hidden',
              width: '100%',
            }}
          >
            <div
              style={{
                padding: '14px 18px',
                borderBottom: '1px solid var(--border)',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <div style={{ fontWeight: 600, fontSize: 14 }}>Upcoming time off</div>
              <div style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-2)' }}>
                Next 30 days
              </div>
            </div>
            <div style={{ padding: '16px 18px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span><b>Jun 22 – 26</b> · Annual</span>
                <Badge variant="warning" dot>Pending</Badge>
              </div>
              <div style={{ height: 1, background: 'var(--border)', margin: '10px 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span><b>Jul 14</b> · Birthday</span>
                <Badge variant="success" dot>Approved</Badge>
              </div>
            </div>
          </div>
        </PrimitiveCard>

        {/* Command palette */}
        <PrimitiveCard
          name="Command palette"
          tag="Organism · AI"
          desc="⌘K. Search, prompts, and ILMU's tool-using answers in one surface."
          dark
          uses={[
            { stage: 'molecule', label: 'Search field' },
            { stage: 'molecule', label: 'Tool call' },
          ]}
        >
          <div
            style={{
              width: '100%',
              maxWidth: 380,
              background: 'var(--surface)',
              borderRadius: 11,
              border: '1px solid var(--border)',
              overflow: 'hidden',
              boxShadow: 'var(--shadow-lg)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '12px 14px',
                borderBottom: '1px solid var(--border)',
              }}
            >
              <div
                style={{ width: 14, height: 14, borderRadius: '50%', background: 'var(--space-cadet)' }}
              />
              <span style={{ fontSize: 14, color: 'var(--text-2)' }}>
                Ask ILMU, or jump to anything…
              </span>
              <Kbd>esc</Kbd>
            </div>
            <div
              style={{
                padding: '8px 14px',
                fontFamily: 'var(--font-mono)',
                fontSize: 9.5,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: 'var(--text-3)',
                fontWeight: 600,
              }}
            >
              Ask ILMU
            </div>
            {[
              'When can I take 5 days off without conflict?',
              'How many annual days do I have left?',
            ].map((q) => (
              <div
                key={q}
                style={{
                  padding: '6px 14px',
                  fontSize: 12.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <span
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: 5,
                    background: 'var(--pear-tint)',
                    flexShrink: 0,
                  }}
                />
                {q}
              </div>
            ))}
          </div>
        </PrimitiveCard>

        {/* Modal */}
        <PrimitiveCard
          name="Modal"
          tag="Organism"
          desc="Header + scroll body + footer with primary/ghost actions."
          uses={[{ stage: 'atom', label: 'Button' }]}
        >
          <div
            style={{
              background: 'rgba(32,41,69,.08)',
              borderRadius: 8,
              padding: 16,
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <div
              style={{
                background: 'var(--surface)',
                borderRadius: 14,
                boxShadow: 'var(--shadow-lg)',
                width: '100%',
                maxWidth: 340,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  padding: '14px 16px',
                  borderBottom: '1px solid var(--border)',
                  fontWeight: 600,
                  fontSize: 13,
                }}
              >
                Apply for leave
              </div>
              <div
                style={{
                  padding: '14px 16px',
                  fontSize: 12.5,
                  color: 'var(--text-2)',
                }}
              >
                Choose your dates and leave type. ILMU will check coverage.
              </div>
              <div
                style={{
                  padding: '10px 16px',
                  borderTop: '1px solid var(--border)',
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 8,
                }}
              >
                <Button variant="secondary" size="sm">Cancel</Button>
                <Button variant="navy" size="sm">Submit</Button>
              </div>
            </div>
          </div>
        </PrimitiveCard>
      </Grid2>
    </section>
  )
}
